// 2026-06-20 (user request) — Halaman Ledger Bank (HALAMAN PENUH, bukan popup).
// Dibuka dari Kas & Bank → History Bank → tombol "Ledger". Bisa di-export ke Excel.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Download } from "lucide-react";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { toast } from "sonner";

export default function BankLedgerPage() {
  const { accountId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bank-history/${accountId}/ledger`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Gagal memuat ledger");
      } finally { setLoading(false); }
    })();
  }, [accountId]);

  const acc = data?.account || {};
  const txns = data?.transactions || [];

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get(`/bank-history/${accountId}/ledger/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ledger_${(acc.bank_name || "bank").replace(/\s+/g, "_")}_${acc.account_no || ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Ledger berhasil diexport");
    } catch {
      toast.error("Gagal mengexport ledger");
    } finally { setExporting(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="bank-ledger-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button type="button" data-testid="ledger-back-btn" onClick={() => nav("/kas-bank?tab=history-bank")}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A] mb-1.5 transition-colors">
            <ArrowLeft size={11} /> Kembali ke Riwayat Bank
          </button>
          <div className="overline text-[10px] text-slate-500 tracking-widest">KAS &amp; BANK — HISTORY BANK</div>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A] flex items-center gap-2" data-testid="ledger-title">
            <BookOpen size={22} className="text-[#FF5A1F]" /> Ledger — {acc.bank_name || "…"}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5 tabular-nums" data-testid="ledger-subtitle">
            {acc.account_no || ""} · mutasi urut tanggal dengan saldo berjalan
          </p>
        </div>
        <button type="button" data-testid="ledger-export-btn" onClick={exportXlsx} disabled={exporting || loading || !!error}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50 transition-colors">
          <Download size={12} /> {exporting ? "Mengexport…" : "Export Excel"}
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-xs italic text-slate-400" data-testid="ledger-loading">Memuat ledger…</div>
      ) : error ? (
        <div className="bg-white border border-slate-200 p-6 text-center text-[11px] text-slate-500" data-testid="ledger-error">{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Saldo Awal</div>
              <div className="font-display text-lg font-bold text-slate-700 tabular-nums" data-testid="ledger-opening">{formatIDR(data.opening_balance || 0)}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Penerimaan Invoice</div>
              <div className="font-display text-lg font-bold text-emerald-700 tabular-nums" data-testid="ledger-total-invoice">{formatIDR(data.total_masuk_invoice || 0)}</div>
              <div className="text-[9px] text-slate-400 tabular-nums">{data.mutasi_count_invoice || 0} transaksi</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Pemasukan Lainnya</div>
              <div className="font-display text-lg font-bold text-amber-700 tabular-nums" data-testid="ledger-total-other">{formatIDR(data.total_masuk_other || 0)}</div>
              <div className="text-[9px] text-slate-400 tabular-nums">{data.mutasi_count_other || 0} transaksi</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Total Mutasi Masuk</div>
              <div className="font-display text-lg font-bold text-emerald-700 tabular-nums" data-testid="ledger-total-masuk">{formatIDR(data.total_masuk)}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Total Keluar</div>
              <div className="font-display text-lg font-bold text-rose-700 tabular-nums" data-testid="ledger-total-keluar">{formatIDR(data.total_keluar)}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Saldo Tercatat</div>
              <div className={`font-display text-lg font-bold tabular-nums ${data.saldo_akhir < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`} data-testid="ledger-saldo">{formatIDR(data.saldo_akhir)}</div>
            </div>
          </div>

          {/* 2026-06-20 (user request): disclaimer — BUKAN saldo aktual rekening
              bank — + waktu pembaruan terakhir. Istilah seragam: Saldo Tercatat. */}
          <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="ledger-disclaimer">
            Nilai ini dihitung dari transaksi masuk dan keluar yang dicatat di sistem, bukan saldo aktual rekening bank.
            {" · "}Pembaruan terakhir: <b className="tabular-nums" data-testid="ledger-last-update">{data.last_mutation_date ? fmtDate(data.last_mutation_date) : "—"}</b>
          </div>

          <div className="bg-white border border-slate-200 overflow-hidden">
            {txns.length === 0 && !(data.opening_balance > 0) ? (
              <div className="p-8 text-center text-[11px] text-slate-400" data-testid="ledger-empty">Belum ada mutasi tercatat pada rekening ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="ledger-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Tanggal</th>
                      <th className="text-left px-3 py-2">Keterangan</th>
                      <th className="text-left px-3 py-2">Proyek</th>
                      <th className="text-left px-3 py-2">Klasifikasi</th>
                      <th className="text-right px-3 py-2">Masuk</th>
                      <th className="text-right px-3 py-2">Keluar</th>
                      <th className="text-right px-3 py-2">Saldo Tercatat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 2026-06-20 (user request): baris pertama = Saldo Awal, ala e-statement bank. */}
                    <tr className="border-b border-slate-200 bg-slate-50/70" data-testid="ledger-row-opening">
                      <td className="px-3 py-1.5 tabular-nums text-slate-400">—</td>
                      <td className="px-3 py-1.5 font-semibold text-slate-700 italic">Saldo Awal</td>
                      <td className="px-3 py-1.5 text-slate-400">—</td>
                      <td className="px-3 py-1.5 text-slate-400">—</td>
                      <td className="px-3 py-1.5"></td>
                      <td className="px-3 py-1.5"></td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-bold text-[#0B1D3A]">{formatIDR(data.opening_balance || 0)}</td>
                    </tr>
                    {txns.map((t, i) => {
                      const CLS = {
                        invoice_counted: { label: "Penerimaan Invoice", cls: "bg-emerald-50 border-emerald-300 text-emerald-700" },
                        invoice_excluded_test: { label: "Invoice TEST", cls: "bg-slate-100 border-slate-300 text-slate-500" },
                        invoice_excluded_internal: { label: "Invoice Internal", cls: "bg-slate-100 border-slate-300 text-slate-500" },
                        invoice_excluded_retention: { label: "Klaim Retensi", cls: "bg-amber-50 border-amber-300 text-amber-700" },
                        petty_cash_return: { label: "Pengembalian Kas", cls: "bg-sky-50 border-sky-300 text-sky-700" },
                      };
                      const meta = t.direction === "in" ? (CLS[t.classification] || { label: "—", cls: "" }) : null;
                      return (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${i}`}>
                        <td className="px-3 py-1.5 tabular-nums text-slate-600 whitespace-nowrap">{t.date ? fmtDate(t.date) : "—"}</td>
                        <td className="px-3 py-1.5 text-slate-800">{t.description}</td>
                        <td className="px-3 py-1.5 text-slate-500 max-w-[220px] truncate" title={t.project_name}>{t.project_name}</td>
                        <td className="px-3 py-1.5">
                          {meta ? <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold border ${meta.cls}`} data-testid={`ledger-cls-${i}`}>{meta.label}</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{t.direction === "in" ? formatIDR(t.amount) : ""}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{t.direction === "out" ? formatIDR(t.amount) : ""}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${t.running_balance < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`}>{formatIDR(t.running_balance)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
